import { useState, type ReactElement } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { SELECTABLE_MODELS, type ReasoningEffortLevel } from '@hector/shared-types';
import { cn } from '@/lib/utils';
import { useSuperAdmin } from '@/hooks/useSuperAdmin';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  clearCaseDiscoveryModelPreference,
  getCaseDiscoveryModelPreference,
  setCaseDiscoveryModelPreference,
} from '../utils/caseDiscoveryModelPreference';

const DEFAULT_SENTINEL = '__default__' as const;

const REASONING_LEVELS: { value: ReasoningEffortLevel; label: string }[] = [
  { value: 'none', label: 'None' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'xhigh', label: 'X-High' },
];

function reasoningLabel(value: ReasoningEffortLevel): string {
  return REASONING_LEVELS.find((level) => level.value === value)?.label ?? value;
}

interface CaseDiscoveryModelSelectorProps {
  className?: string;
}

export function CaseDiscoveryModelSelector({ className }: CaseDiscoveryModelSelectorProps): ReactElement | null {
  const isSuperAdmin = useSuperAdmin();
  if (!isSuperAdmin) return null;

  return <CaseDiscoveryModelSelectorInner className={className} />;
}

function CaseDiscoveryModelSelectorInner({ className }: CaseDiscoveryModelSelectorProps): ReactElement {
  const initial = getCaseDiscoveryModelPreference();
  const [selectedSlug, setSelectedSlug] = useState<string>(initial?.model ?? DEFAULT_SENTINEL);
  const [selectedReasoning, setSelectedReasoning] = useState<ReasoningEffortLevel | undefined>(
    initial?.reasoningEffort,
  );
  const activeModel = SELECTABLE_MODELS.find((model) => model.slug === selectedSlug) ?? null;

  function selectDefault(): void {
    clearCaseDiscoveryModelPreference();
    setSelectedSlug(DEFAULT_SENTINEL);
    setSelectedReasoning(undefined);
  }

  function selectModel(slug: string, effort: ReasoningEffortLevel | undefined): void {
    const found = SELECTABLE_MODELS.find((model) => model.slug === slug);
    if (!found || !found.chat) return;

    const reasoningEffort = found.reasoning ? effort : undefined;
    setSelectedSlug(found.slug);
    setSelectedReasoning(reasoningEffort);
    setCaseDiscoveryModelPreference({
      model: found.slug as Parameters<typeof setCaseDiscoveryModelPreference>[0]['model'],
      ...(reasoningEffort !== undefined ? { reasoningEffort } : {}),
    });
  }

  const tagLabel = activeModel === null
    ? 'PROD'
    : activeModel.reasoning && selectedReasoning !== undefined
      ? `${activeModel.label} (${reasoningLabel(selectedReasoning)})`
      : activeModel.label;

  return (
    <div className={cn('inline-flex items-center gap-1.5 whitespace-nowrap', className)}>
      <span className="shrink-0 text-[10px] font-mono uppercase tracking-[0.08em] text-grey-400 select-none">
        LLM&nbsp;:
      </span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Sélection du modèle LLM Case Discovery"
            className="inline-flex h-6 max-w-full items-center gap-1.5 rounded border border-grey-200 bg-white px-2 text-[10px] font-mono uppercase tracking-[0.08em] text-grey-600 outline-none transition-colors hover:border-grey-300 hover:text-charcoal"
          >
            <span className="truncate max-w-[220px]">{tagLabel}</span>
            <ChevronDown className="h-3 w-3 shrink-0" />
          </button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="min-w-[230px]">
          <DropdownMenuItem
            onSelect={selectDefault}
            className="gap-1.5 text-[10px] font-mono uppercase tracking-[0.08em] text-grey-500"
          >
            <span className="w-3 shrink-0">
              {selectedSlug === DEFAULT_SENTINEL && <Check className="h-3 w-3 text-charcoal" />}
            </span>
            PROD
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          {SELECTABLE_MODELS.map((model) => {
            const disabled = !model.chat;
            const isSelected = selectedSlug === model.slug;
            const row = (
              <>
                <span className="w-3 shrink-0">
                  {isSelected && <Check className="h-3 w-3 text-charcoal" />}
                </span>
                <span className="truncate text-xs text-charcoal">{model.label}</span>
                <span className="ml-auto text-[9px] font-mono uppercase tracking-[0.08em] text-grey-400">
                  {model.zone}
                </span>
              </>
            );

            if (disabled) {
              return (
                <DropdownMenuItem key={model.slug} disabled className="gap-1.5">
                  {row}
                </DropdownMenuItem>
              );
            }

            if (model.reasoning) {
              return (
                <DropdownMenuSub key={model.slug}>
                  <DropdownMenuSubTrigger className="gap-1.5 text-xs">
                    {row}
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent className="min-w-[110px]">
                    {REASONING_LEVELS.map((level) => (
                      <DropdownMenuItem
                        key={level.value}
                        onSelect={() => selectModel(model.slug, level.value)}
                        className="gap-1.5 text-xs"
                      >
                        <span className="w-3 shrink-0">
                          {isSelected && selectedReasoning === level.value && (
                            <Check className="h-3 w-3 text-charcoal" />
                          )}
                        </span>
                        {level.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              );
            }

            return (
              <DropdownMenuItem
                key={model.slug}
                onSelect={() => selectModel(model.slug, undefined)}
                className="gap-1.5"
              >
                {row}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
